import React, { useEffect, useMemo, useReducer } from 'react';
import Select from '@rio-cloud/rio-uikit/lib/es/Select';
import OverlayTrigger from '@rio-cloud/rio-uikit/lib/es/OverlayTrigger';
import Tooltip from '@rio-cloud/rio-uikit/lib/es/Tooltip';
import { FormattedMessage } from 'react-intl';
import {
    useFetchDeliverableIdsQuery,
    useLazyFetchDeliverableVersionsQuery,
    useLazyValidateDeliverableQuery
} from '~/api/deliverables/DeliverablesManagement.api';
import { toShortSemanticVersion } from '~/features/base/utils/versionNumberConverter';
import { SemanticVersion } from '~/api/models';
import { DELIVERABLE_TYPE_CM4G, DELIVERABLE_TYPE_FILE, DELIVERABLE_TYPE_BUNDLE } from '~/features/deliverables/constants/deliverablesParameters';
import { DeliverableSearchCriteriaDeliverableTypeEnum } from '~/api/models/deliverable-search-criteria';
import classNames from 'classnames';
import { prepareDeliverablePayload } from '~/features/deliverables/sagas/deliverableSaga';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { VOCU, ESSENTIAL_IMAGE } from '~/features/deliverables/constants/DeliverableSubtype';

const emptyRow = {
    deliverableId: '',
    deliverableType: '',
    deliverableVersion: {
        major: '',
        minor: '',
        patch: '',
    },
    sequence: '',
};

interface DeliverableVersionModel {
    [p: string]: { id: string, label: string, value: SemanticVersion }[] | undefined;
}

const SortableRow = ({ value, index, deliverableOptions, deliverableVersions, deliverableHasWarning, getReleaseState, onDeliverableVersionChange, onFieldsChange, onDeleteDeliverableFilterFormGroup, valuesLength }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: index });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    return (
        <tr ref={setNodeRef} style={style} className={classNames({'has-warning': deliverableHasWarning(value)})}>
            <td className='text-center'>{index + 1}</td>
            <td>
                <Select
                    className={'warning'}
                    useFilter
                    useClear
                    options={deliverableOptions}
                    value={[value?.deliverableId]}
                    onChange={(val) => onDeliverableVersionChange(val, index)}/>
                {deliverableHasWarning(value) &&
                    <div className={'margin-top-5'}>
                        <span className='rioglyph rioglyph-info-sign text-color-warning text-size-15 margin-right-5'/>
                        <span className={'text-color-warning text-size-12'}>
                            <FormattedMessage
                                id={'intl-msg:deliverables.bundleDeliverableReleaseStateWarningForm'}
                                values={{releaseState: getReleaseState(value)}}/>
                        </span>
                    </div>}
            </td>
            <td>
                <Select
                    useFilter
                    useClear
                    disabled={!value.deliverableId}
                    options={deliverableVersions[value.deliverableId] || []}
                    value={[toShortSemanticVersion(value.deliverableVersion)]}
                    onChange={(val) => onFieldsChange({['deliverableVersion']: val?.value}, index)}/>
            </td>
            <td className='text-center'>
                <button type="button" className="btn btn-danger btn-xs"
                         onClick={() => onDeleteDeliverableFilterFormGroup(index)}
                         disabled={valuesLength === 1}>
                    <span className="rioglyph rioglyph-trash"/>
                </button>
            </td>
            <td className='text-center' style={{cursor: 'grab'}} {...attributes} {...listeners}>
                <span className="rioglyph rioglyph-th-list" style={{fontSize: '18px'}}/>
            </td>
        </tr>
    );
};

const BundleDeliverables = ({deliverables, values = [emptyRow], onChange, hasWarning, model, bundleType}) => {
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8,
            },
        })
    );

    const handleDragEnd = (event) => {
        const { active, over } = event;
        if (over && active.id !== over.id) {
            const oldIndex = active.id;
            const newIndex = over.id;
            const list = [...values];
            const [movedItem] = list.splice(oldIndex, 1);
            list.splice(newIndex, 0, movedItem);
            updateSequences(list);
        }
    };

    const isVocuBundle = bundleType === VOCU;

    const { data: standaloneCM4GList } = useFetchDeliverableIdsQuery({
        deliverableType: DeliverableSearchCriteriaDeliverableTypeEnum.CM4G,
        isStandalone: false
    }, { skip: isVocuBundle });
    const { data: standaloneFilesList } = useFetchDeliverableIdsQuery({
        deliverableType: DeliverableSearchCriteriaDeliverableTypeEnum.FILE,
        isStandalone: false
    }, { skip: isVocuBundle });
    const { data: vocuFilesList } = useFetchDeliverableIdsQuery({
        deliverableType: DeliverableSearchCriteriaDeliverableTypeEnum.FILE,
        isStandalone: false,
        fileType: VOCU,
    } as any, { skip: !isVocuBundle });
    const { data: essentialImageBundlesList } = useFetchDeliverableIdsQuery({
        deliverableType: DeliverableSearchCriteriaDeliverableTypeEnum.BUNDLE,
        bundleType: ESSENTIAL_IMAGE,
    } as any, { skip: !isVocuBundle });

    const deliverableHasWarning = (value) => {
        return hasWarning ? !!hasWarning?.find((warn) => warn.deliverableId === value.deliverableId && toShortSemanticVersion(warn.deliverableVersion) === toShortSemanticVersion(value.deliverableVersion)) : false;
    };
    const getReleaseState = (value) => {
        return hasWarning?.find((warn) => warn.deliverableId === value.deliverableId && toShortSemanticVersion(warn.deliverableVersion) === toShortSemanticVersion(value.deliverableVersion))?.releaseState;
    };
    const [validateDeliverable] = useLazyValidateDeliverableQuery();

    useEffect(() => {
        if (model.deliverableType) {
            const filteredModel = prepareDeliverablePayload({ ...model });
            validateDeliverable({
                deliverable: filteredModel,
                isEdit: true
            });

        }
    }, [model]);

    const deliverableOptions = useMemo(() => {
        if (isVocuBundle) {
            const fileWithType = vocuFilesList?.content?.map(deliverableId => ({
                id: deliverableId,
                type: DELIVERABLE_TYPE_FILE.toUpperCase(),
                label: `${deliverableId} (${DELIVERABLE_TYPE_FILE.toUpperCase()})`
            })) || [];
            const essentialImageBundles = essentialImageBundlesList?.content?.map(deliverableId => ({
                id: deliverableId,
                type: DELIVERABLE_TYPE_BUNDLE.toUpperCase(),
                label: `${deliverableId} (${DELIVERABLE_TYPE_BUNDLE.toUpperCase()})`
            })) || [];
            return [...fileWithType, ...essentialImageBundles];
        }

        const cmg4WithType = standaloneCM4GList?.content?.map(deliverableId => ({
            id: deliverableId,
            type: DELIVERABLE_TYPE_CM4G.toUpperCase(),
            label: `${deliverableId} (${DELIVERABLE_TYPE_CM4G.toUpperCase()})`
        })) || [];
        const fileWithType = standaloneFilesList?.content?.map(deliverableId => ({
            id: deliverableId,
            type: DELIVERABLE_TYPE_FILE.toUpperCase(),
            label: `${deliverableId} (${DELIVERABLE_TYPE_FILE.toUpperCase()})`
        })) || [];
        return [...cmg4WithType, ...fileWithType];
    }, [isVocuBundle, standaloneCM4GList, standaloneFilesList, vocuFilesList, essentialImageBundlesList]);

    const [getDeliverableVersions] = useLazyFetchDeliverableVersionsQuery();

    const [deliverableVersions, setDeliverableVersions] = useReducer((prev, next) => {
        return {...prev, ...next}
    }, {} as DeliverableVersionModel);


    useEffect(() => {
        if (!values || values?.length === 0 || values[0].deliverableId === '') {
            onChange([emptyRow]);
        } else {
            values.forEach(deliverable => getVersions(deliverable.deliverableId, deliverable.deliverableType));
        }
    }, []);

    const onFieldsChange = (newValue, index) => {
        if (index != undefined) {
            let deliverables = [...values];
            deliverables[index] = {...values[index], ...newValue || ''};
            onChange(deliverables);
        }
    };

    const onAddDeliverableFilterFormGroup = () => {
        onChange(values.concat([{...emptyRow}]));
    }

    const getVersions = (deliverableId = '', deliverableType = '') => {
        getDeliverableVersions({deliverableId, deliverableType}).then(result => {
            const versionsList = result?.data?.content.map(version => {
                return {
                    label: toShortSemanticVersion(version),
                    value: version,
                    id: toShortSemanticVersion(version)
                }
            })
            setDeliverableVersions({...deliverableVersions, [deliverableId]: versionsList});
        })
    }

    const onDeliverableVersionChange = (value, index) => {
        let deliverable = {};
        if (index != undefined) {
            getVersions(value?.id, value?.type);
            deliverable = {
                ['deliverableType']: value?.type,
                ['deliverableId']: value?.id,
                };
        }
        onFieldsChange({...deliverable, ['deliverableVersion']: {
                major:'',
                minor:'',
                patch:'',
            }}, index);

    }

    const onDeleteDeliverableFilterFormGroup = (index) => {
        let list = [...values];
        list.splice(index, 1);
        updateSequences(list);
    }

    const moveDeliverable = (index, direction) => {
        const newIndex = index + direction;
        if (newIndex < 0 || newIndex >= values.length) return;

        const list = [...values];
        [list[index], list[newIndex]] = [list[newIndex], list[index]];
        updateSequences(list);
    }

    const updateSequences = (list) => {
        const updatedList = list.map((item, idx) => ({
            ...item,
            sequence: idx + 1
        }));
        onChange(updatedList);
    }

    return <>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <div className='margin-bottom-20'>
                <table className='table table-bordered'>
                    <thead>
                        <tr>
                            <th style={{width: '50px'}}>
                                <OverlayTrigger placement="top" overlay={<Tooltip id="sequence-tooltip"><FormattedMessage id='intl-msg:deliverables.bundleSequenceInfo'/></Tooltip>}>
                                    <span className="rioglyph rioglyph-info-sign text-color-info" style={{marginLeft: '5px'}}/>
                                </OverlayTrigger>
                            </th>
                            <th><FormattedMessage id='intl-msg:deliverableId'/></th>
                            <th style={{width: '150px'}}><FormattedMessage id='intl-msg:version'/></th>
                            <th style={{width: '80px'}}><FormattedMessage id='intl-msg:actions'/></th>
                            <th style={{width: '50px'}}></th>
                        </tr>
                    </thead>
                    <tbody>
                        <SortableContext items={values.map((_, idx) => idx)} strategy={verticalListSortingStrategy}>
                            {values?.map((value, index) => (
                                <SortableRow
                                    key={index}
                                    value={value}
                                    index={index}
                                    deliverableOptions={deliverableOptions}
                                    deliverableVersions={deliverableVersions}
                                    deliverableHasWarning={deliverableHasWarning}
                                    getReleaseState={getReleaseState}
                                    onDeliverableVersionChange={onDeliverableVersionChange}
                                    onFieldsChange={onFieldsChange}
                                    onDeleteDeliverableFilterFormGroup={onDeleteDeliverableFilterFormGroup}
                                    valuesLength={values.length}
                                />
                            ))}
                        </SortableContext>
                    </tbody>
                </table>
            </div>
        </DndContext>
        {values.length > 0 && <button type="button" className="padding-left-20 padding-right-20 btn btn-success"
                 onClick={onAddDeliverableFilterFormGroup}>
            <span className="text-capitalize"><FormattedMessage id='intl-msg:changeOperation.add'/></span>
        </button>}
    </>;
}

export default BundleDeliverables;
